import { useMemo } from 'react';
import useConfiguration from 'hooks/useConfiguration';
import { PROVIDER_TYPE } from 'constants/global';

// Configuration keys managed from the dashboard (reference format screens):
// whitespace-separated logo URLs per cash provider.
export const PARTNER_LOGOS_CONFIG_KEY = {
  [PROVIDER_TYPE.PAYCASH]: 'partners',
  [PROVIDER_TYPE.PUNTORED]: 'partnersPuntored',
};

const logoNameFromUrl = (url) => {
  const file = url.split(/[?#]/)[0].split('/').pop() || '';
  let decoded = file;
  try {
    decoded = decodeURIComponent(file);
  } catch (error) {
    // Malformed escape sequences: keep the raw file name.
  }
  return decoded.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim();
};

/**
 * Parses the raw configuration value into logo entries.
 * Repeated URLs are dropped (the dashboard value may contain duplicates).
 * @param {string|null} raw
 * @returns {{ url: string, name: string }[]}
 */
export const parsePartnerLogos = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  return [...new Set(raw.trim().split(/\s+/).filter(Boolean))]
    .map((url) => ({ url, name: logoNameFromUrl(url) }));
};

/**
 * Store logos where a cash reference can be paid, as configured in the dashboard.
 * Providers without a configuration key (card methods) return an empty list.
 * @param {string|null} provider PROVIDER_TYPE value
 * @returns {{ url: string, name: string }[]}
 */
export default function usePartnerLogos(provider) {
  const { getConfigurationValue } = useConfiguration();
  const key = PARTNER_LOGOS_CONFIG_KEY[provider];

  return useMemo(
    () => (key ? parsePartnerLogos(getConfigurationValue(key)) : []),
    [getConfigurationValue, key],
  );
}
